import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { MetricDataPoint, MetricRangeDataPoint } from '../../gql/generated';
import {
  BOOLEAN_SIGNAL_THRESHOLD,
  toBinarySignalState,
} from '../../utils/booleanSignals';
import { CHART_PLOT_HEIGHT, X_GRID_FRACTIONS } from './chartGeometry';
import type { TimeBounds } from './timeRangeDomain';

interface MachineTimeBarChartProps {
  /** Data points - can be single machine (MetricDataPoint[]) or multi-machine (MetricRangeDataPoint[]) */
  dataPoints: MetricDataPoint[] | MetricRangeDataPoint[];
  /** Unit of measurement (e.g., '°', 'bar') */
  unit: string;
  /** Alarm thresholds to display as reference lines */
  alarmThresholds?: {
    minimum?: number;
    maximum?: number;
  };
  /** Optional timestamp (ms) when the alarm was triggered - shows indicator on graph */
  alarmTriggerTime?: number;
  /** The window the X-axis spans, resolved by the caller so bars and axis labels agree */
  timeBounds: TimeBounds;
  /** Render chart in boolean alarm mode (off/on axis). */
  isBooleanSignal?: boolean;
}

/**
 * MachineTimeBarChart - Displays metric data as time-aligned bars
 *
 * Unlike the regular bar chart, this positions bars according to their
 * actual timestamps, so gaps in data appear as gaps in the chart.
 */
export function MachineTimeBarChart({
  dataPoints,
  unit,
  alarmThresholds,
  alarmTriggerTime,
  timeBounds,
  isBooleanSignal = false,
}: MachineTimeBarChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const uniqueId = useId().replace(/:/g, '');
  const booleanGradientId = `machine-boolean-gradient-${uniqueId}`;
  const [containerWidth, setContainerWidth] = useState(400);

  // Update container width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  // Chart dimensions
  const chartHeight = CHART_PLOT_HEIGHT;
  const chartPadding = { top: 4, right: 0, bottom: 0, left: 0 };

  const normalizedDataPoints = useMemo(
    () =>
      isBooleanSignal
        ? dataPoints.map((point) => ({
            ...point,
            min: toBinarySignalState(point.min),
            max: toBinarySignalState(point.max),
            average: toBinarySignalState(point.average),
          }))
        : dataPoints,
    [dataPoints, isBooleanSignal]
  );

  const normalizedThresholds = useMemo(() => {
    if (!isBooleanSignal || !alarmThresholds) return alarmThresholds;
    return {
      minimum:
        alarmThresholds.minimum !== undefined
          ? BOOLEAN_SIGNAL_THRESHOLD
          : alarmThresholds.minimum,
      maximum:
        alarmThresholds.maximum !== undefined
          ? BOOLEAN_SIGNAL_THRESHOLD
          : alarmThresholds.maximum,
    };
  }, [alarmThresholds, isBooleanSignal]);

  // Calculate Y-axis bounds — include alarm thresholds so their lines are always visible
  const { yMin, yMax, ticks } = useMemo(() => {
    if (isBooleanSignal) {
      return { yMin: -0.1, yMax: 1.1, ticks: [0, 1] };
    }

    const dataMin = Math.min(
      ...normalizedDataPoints.map((d) => d.min),
      normalizedThresholds?.minimum ?? Infinity,
      normalizedThresholds?.maximum ?? Infinity
    );
    const dataMax = Math.max(
      ...normalizedDataPoints.map((d) => d.max),
      normalizedThresholds?.minimum ?? -Infinity,
      normalizedThresholds?.maximum ?? -Infinity
    );

    const padding = (dataMax - dataMin) * 0.1 || 5;
    let yMin = Math.floor(dataMin - padding);
    let yMax = Math.ceil(dataMax + padding);

    const range = yMax - yMin;
    let step: number;
    if (range <= 5) {
      step = 1;
    } else if (range <= 10) {
      step = 2;
    } else if (range <= 25) {
      step = 5;
    } else {
      step = Math.ceil(range / 5 / 10) * 10;
    }

    yMin = Math.floor(yMin / step) * step;
    yMax = Math.ceil(yMax / step) * step;

    const ticks: number[] = [];
    for (let v = yMin; v <= yMax; v += step) {
      ticks.push(v);
    }

    return { yMin, yMax, ticks };
  }, [normalizedDataPoints, normalizedThresholds, isBooleanSignal]);

  const timeMin = timeBounds.from;
  const timeMax = timeBounds.to;

  // Calculate bar width based on minimum time gap between data points
  const barWidth = useMemo(() => {
    if (normalizedDataPoints.length < 2) return 8;

    // Sort data points by time and find the minimum gap
    const sortedTimes = [...normalizedDataPoints]
      .map((d) => d.time)
      .sort((a, b) => a - b);
    let minGap = Infinity;
    for (let i = 1; i < sortedTimes.length; i++) {
      const gap = sortedTimes[i] - sortedTimes[i - 1];
      if (gap > 0 && gap < minGap) {
        minGap = gap;
      }
    }

    // Convert minimum time gap to pixels
    const timeRange = timeMax - timeMin;
    const pixelsPerMs = containerWidth / timeRange;
    const gapInPixels = minGap * pixelsPerMs;

    // Bar width is 80% of the minimum gap (leaving 20% spacing)
    const idealWidth = gapInPixels * 0.8;

    // Cap between 2 and 12 pixels
    return Math.max(2, Math.min(12, idealWidth));
  }, [normalizedDataPoints, containerWidth, timeMin, timeMax]);

  // Helper to convert Y value to pixel position
  const yToPixel = (value: number, height: number) => {
    const plotHeight = height - chartPadding.top - chartPadding.bottom;
    const normalized = (value - yMin) / (yMax - yMin);
    return chartPadding.top + plotHeight * (1 - normalized);
  };

  // Helper to convert time to X pixel position
  const timeToPixel = (time: number, width: number) => {
    if (timeMax === timeMin) return width / 2;
    const plotWidth = width - chartPadding.left - chartPadding.right;
    const normalized = (time - timeMin) / (timeMax - timeMin);
    return chartPadding.left + plotWidth * normalized;
  };

  // Colors
  const colors = {
    inRange: 'rgb(123, 187, 243)',
    inRangeStroke: 'rgb(222, 239, 254)',
    belowMin: 'rgb(239, 68, 68)',
    belowMinStroke: 'rgb(254, 226, 226)',
    aboveMax: 'rgb(239, 68, 68)',
    aboveMaxStroke: 'rgb(254, 226, 226)',
    threshold: 'rgb(234, 88, 12)',
    grid: 'rgb(231, 234, 238)',
    alarmTrigger: 'rgb(194, 31, 31)', // Red for alarm trigger indicator
    alarmTriggerFill: 'rgb(254, 226, 226)',
  };

  const hasAlarmTriggerInRange =
    alarmTriggerTime !== undefined &&
    alarmTriggerTime >= timeMin &&
    alarmTriggerTime <= timeMax;

  const alarmTriggerX = hasAlarmTriggerInRange
    ? timeToPixel(alarmTriggerTime, containerWidth)
    : null;

  const alarmTriggerTimeLabel = hasAlarmTriggerInRange
    ? new Date(alarmTriggerTime).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : null;

  const alarmLabelShouldRenderOnLeft =
    alarmTriggerX !== null && alarmTriggerX > containerWidth - 70;

  const alarmLabelX =
    alarmTriggerX === null
      ? null
      : alarmLabelShouldRenderOnLeft
        ? Math.max(6, alarmTriggerX - 12)
        : Math.min(containerWidth - 6, alarmTriggerX + 12);

  const booleanSeries = useMemo(() => {
    if (!isBooleanSignal || normalizedDataPoints.length === 0) return null;

    const plotWidth = containerWidth - chartPadding.left - chartPadding.right;
    const plotHeight = chartHeight - chartPadding.top - chartPadding.bottom;

    const toX = (time: number) => {
      if (timeMax === timeMin) return containerWidth / 2;
      const normalized = (time - timeMin) / (timeMax - timeMin);
      return chartPadding.left + plotWidth * normalized;
    };

    const toY = (value: number) => {
      const normalized = (value - yMin) / (yMax - yMin);
      return chartPadding.top + plotHeight * (1 - normalized);
    };

    const orderedPoints = [...normalizedDataPoints].sort(
      (a, b) => a.time - b.time
    );
    const firstPoint = orderedPoints[0];
    const firstX = toX(firstPoint.time);
    const firstY = toY(toBinarySignalState(firstPoint.average));

    let path = `M ${firstX} ${firstY}`;

    for (let index = 1; index < orderedPoints.length; index++) {
      const point = orderedPoints[index];
      const x = toX(point.time);
      const y = toY(toBinarySignalState(point.average));

      // Horizontal then vertical segment creates a clean digital transition.
      path += ` H ${x} V ${y}`;
    }

    // Build shaded "ON" regions so the trigger line reads like filled alert bars.
    const onSegments = orderedPoints
      .map((point, index) => {
        const isOn = toBinarySignalState(point.average) === 1;
        if (!isOn) return null;

        const nextPoint = orderedPoints[index + 1];
        const endTime = nextPoint ? nextPoint.time : timeMax;
        if (endTime <= point.time) return null;

        const startX = toX(point.time);
        const endX = toX(endTime);
        const width = endX - startX;
        if (width <= 0) return null;

        return { x: startX, width };
      })
      .filter((segment): segment is { x: number; width: number } =>
        Boolean(segment)
      );

    return {
      path,
      onSegments,
      onY: toY(1),
      offY: toY(0),
    };
  }, [
    isBooleanSignal,
    normalizedDataPoints,
    containerWidth,
    chartHeight,
    timeMin,
    timeMax,
    yMin,
    yMax,
    chartPadding.top,
    chartPadding.right,
    chartPadding.bottom,
    chartPadding.left,
  ]);

  const yAxisLabelWidth = useMemo(() => {
    const longestLabelLength = ticks
      .map((tick) =>
        isBooleanSignal ? (tick === 1 ? 'on' : 'off') : `${tick} ${unit}`
      )
      .reduce((longest, label) => Math.max(longest, label.length), 0);

    const widthInCh = Math.min(20, Math.max(6, longestLabelLength + 1));
    return `${widthInCh}ch`;
  }, [ticks, isBooleanSignal, unit]);

  return (
    <div className='flex items-start gap-3.5 select-none'>
      {/* Y-axis Labels */}
      <div
        className='size-caption text-secondary relative ml-1 w-fit min-w-8 pt-1
          pr-1 text-right'
        style={{
          height: chartHeight,
          width: yAxisLabelWidth,
          minWidth: yAxisLabelWidth,
        }}
      >
        {ticks
          .slice()
          .reverse()
          .map((tick, index) => {
            const positionPercent = ((yMax - tick) / (yMax - yMin)) * 100;
            return (
              <span
                key={index}
                className='absolute right-0 whitespace-nowrap'
                style={{
                  top: `${positionPercent}%`,
                  transform: 'translateY(-50%)',
                }}
              >
                {isBooleanSignal
                  ? tick === 1
                    ? 'on'
                    : 'off'
                  : `${tick} ${unit}`}
              </span>
            );
          })}
      </div>

      {/* Chart */}
      <div ref={containerRef} className='flex-1'>
        <svg
          width='100%'
          height={chartHeight}
          className='block overflow-visible'
        >
          <defs>
            <linearGradient id={booleanGradientId} x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={colors.inRange} stopOpacity={0.35} />
              <stop
                offset='100%'
                stopColor={colors.inRange}
                stopOpacity={0.08}
              />
            </linearGradient>
          </defs>

          {/* Grid lines, dotted like the design */}
          {ticks.map((tick, index) => {
            const y = yToPixel(tick, chartHeight);
            return (
              <line
                key={`grid-${index}`}
                x1='0'
                x2='100%'
                y1={y}
                y2={y}
                stroke={colors.grid}
                strokeWidth={1}
                strokeDasharray='2 2'
              />
            );
          })}
          {X_GRID_FRACTIONS.map((fraction) => (
            <line
              key={`vgrid-${fraction}`}
              x1={`${fraction * 100}%`}
              x2={`${fraction * 100}%`}
              y1={chartPadding.top}
              y2={chartHeight}
              stroke={colors.grid}
              strokeWidth={1}
              strokeDasharray='2 2'
            />
          ))}

          {/* Series */}
          {isBooleanSignal ? (
            <>
              {booleanSeries?.onSegments.map((segment, index) => (
                <rect
                  key={`on-segment-${index}`}
                  x={segment.x}
                  y={Math.min(booleanSeries.onY, booleanSeries.offY)}
                  width={segment.width}
                  height={Math.abs(booleanSeries.offY - booleanSeries.onY)}
                  fill={`url(#${booleanGradientId})`}
                />
              ))}

              {booleanSeries?.path && (
                <path
                  d={booleanSeries.path}
                  fill='none'
                  stroke={colors.inRange}
                  strokeWidth={2.5}
                  strokeLinejoin='round'
                  strokeLinecap='round'
                />
              )}
            </>
          ) : (
            normalizedDataPoints.map((point, index) => {
              const x = timeToPixel(point.time, containerWidth);

              const minThreshold = normalizedThresholds?.minimum;
              const maxThreshold = normalizedThresholds?.maximum;

              // Calculate bar segments based on thresholds
              const segments: Array<{
                y1: number;
                y2: number;
                fill: string;
                stroke: string;
              }> = [];

              const min = point.min;
              const max = point.max;

              if (minThreshold !== undefined && maxThreshold !== undefined) {
                // Both thresholds defined
                if (max < minThreshold) {
                  // Entire bar below minimum - red
                  segments.push({
                    y1: min,
                    y2: max,
                    fill: colors.belowMin,
                    stroke: colors.belowMinStroke,
                  });
                } else if (min > maxThreshold) {
                  // Entire bar above maximum - red
                  segments.push({
                    y1: min,
                    y2: max,
                    fill: colors.aboveMax,
                    stroke: colors.aboveMaxStroke,
                  });
                } else {
                  // Bar spans regions
                  if (min < minThreshold) {
                    segments.push({
                      y1: min,
                      y2: Math.min(max, minThreshold),
                      fill: colors.belowMin,
                      stroke: colors.belowMinStroke,
                    });
                  }

                  const rangeStart = Math.max(min, minThreshold);
                  const rangeEnd = Math.min(max, maxThreshold);
                  if (rangeEnd > rangeStart) {
                    segments.push({
                      y1: rangeStart,
                      y2: rangeEnd,
                      fill: colors.inRange,
                      stroke: colors.inRangeStroke,
                    });
                  }

                  if (max > maxThreshold) {
                    segments.push({
                      y1: Math.max(min, maxThreshold),
                      y2: max,
                      fill: colors.aboveMax,
                      stroke: colors.aboveMaxStroke,
                    });
                  }
                }
              } else if (minThreshold !== undefined) {
                // Only minimum threshold
                if (max < minThreshold) {
                  segments.push({
                    y1: min,
                    y2: max,
                    fill: colors.belowMin,
                    stroke: colors.belowMinStroke,
                  });
                } else if (min < minThreshold) {
                  segments.push({
                    y1: min,
                    y2: minThreshold,
                    fill: colors.belowMin,
                    stroke: colors.belowMinStroke,
                  });
                  segments.push({
                    y1: minThreshold,
                    y2: max,
                    fill: colors.inRange,
                    stroke: colors.inRangeStroke,
                  });
                } else {
                  segments.push({
                    y1: min,
                    y2: max,
                    fill: colors.inRange,
                    stroke: colors.inRangeStroke,
                  });
                }
              } else if (maxThreshold !== undefined) {
                // Only maximum threshold
                if (min > maxThreshold) {
                  segments.push({
                    y1: min,
                    y2: max,
                    fill: colors.aboveMax,
                    stroke: colors.aboveMaxStroke,
                  });
                } else if (max > maxThreshold) {
                  segments.push({
                    y1: min,
                    y2: maxThreshold,
                    fill: colors.inRange,
                    stroke: colors.inRangeStroke,
                  });
                  segments.push({
                    y1: maxThreshold,
                    y2: max,
                    fill: colors.aboveMax,
                    stroke: colors.aboveMaxStroke,
                  });
                } else {
                  segments.push({
                    y1: min,
                    y2: max,
                    fill: colors.inRange,
                    stroke: colors.inRangeStroke,
                  });
                }
              } else {
                // No thresholds - all in range
                segments.push({
                  y1: min,
                  y2: max,
                  fill: colors.inRange,
                  stroke: colors.inRangeStroke,
                });
              }

              return (
                <g key={`bar-${index}`}>
                  {segments.map((seg, segIndex) => {
                    const y1Pixel = yToPixel(seg.y2, chartHeight); // y2 is higher value, lower pixel
                    const y2Pixel = yToPixel(seg.y1, chartHeight); // y1 is lower value, higher pixel
                    const height = Math.max(1, y2Pixel - y1Pixel);

                    return (
                      <rect
                        key={`seg-${segIndex}`}
                        x={x - barWidth / 2}
                        y={y1Pixel}
                        width={barWidth}
                        height={height}
                        fill={seg.fill}
                        stroke={seg.stroke}
                        strokeWidth={0.5}
                      />
                    );
                  })}
                </g>
              );
            })
          )}

          {/* Threshold lines — not applicable for boolean (Machine Alarm) signals */}
          {!isBooleanSignal && normalizedThresholds?.minimum !== undefined && (
            <line
              x1='0'
              x2='100%'
              y1={yToPixel(normalizedThresholds.minimum, chartHeight)}
              y2={yToPixel(normalizedThresholds.minimum, chartHeight)}
              stroke={colors.threshold}
              strokeWidth={2}
              strokeDasharray='5 5'
              opacity={0.75}
            />
          )}
          {!isBooleanSignal && normalizedThresholds?.maximum !== undefined && (
            <line
              x1='0'
              x2='100%'
              y1={yToPixel(normalizedThresholds.maximum, chartHeight)}
              y2={yToPixel(normalizedThresholds.maximum, chartHeight)}
              stroke={colors.threshold}
              strokeWidth={2}
              strokeDasharray='5 5'
              opacity={0.75}
            />
          )}

          {/* Alarm trigger time indicator */}
          {hasAlarmTriggerInRange &&
            alarmTriggerX !== null &&
            alarmLabelX !== null &&
            alarmTriggerTimeLabel !== null && (
              <>
                {/* Vertical line from top to bottom */}
                <line
                  x1={alarmTriggerX}
                  x2={alarmTriggerX}
                  y1={chartPadding.top}
                  y2={chartHeight - chartPadding.bottom}
                  stroke={colors.alarmTrigger}
                  strokeWidth={2}
                />
                {/* Alarm bell icon at top */}
                <g
                  transform={`translate(${alarmTriggerX - 8}, ${chartPadding.top - 16})`}
                >
                  <rect
                    x={0}
                    y={0}
                    width={16}
                    height={16}
                    rx={6}
                    ry={6}
                    fill='rgb(254, 226, 226)'
                    stroke='rgb(220, 38, 38)'
                    strokeWidth={1}
                  />
                  {/* Alarm bell icon */}
                  <path
                    d='M5.5 9C5.5 9.5523 5.0523 10 4.5 10C3.9477 10 3.5 9.5523 3.5 9H5.5ZM5 1.03613C6.6961 1.27879 8 2.7368 8 4.5V7.5H9V8.5H0V7.5H1V4.5C1 2.7368 2.3039 1.27879 4 1.03613V0H5V1.03613ZM4.5 2C3.11929 2 2 3.11929 2 4.5V7.5H7V4.5C7 3.11929 5.8807 2 4.5 2Z'
                    fill='rgb(220, 38, 38)'
                    transform='translate(3.5, 3)'
                  />
                </g>
                <text
                  x={alarmLabelX}
                  y={chartPadding.top - 4}
                  textAnchor={alarmLabelShouldRenderOnLeft ? 'end' : 'start'}
                  dominantBaseline='middle'
                  fill={colors.alarmTrigger}
                  fontSize='11'
                  fontWeight='600'
                >
                  {alarmTriggerTimeLabel}
                </text>
              </>
            )}
        </svg>
      </div>
    </div>
  );
}
